import env from '@/config/env'
import { type TrpcRouter } from '@backend/routes/trpc'
import { type inferRouterInputs, type inferRouterOutputs, TRPCError } from '@trpc/server'
import { httpBatchLink, httpLink, splitLink, TRPCClientError, type TRPCLink } from '@trpc/client'
import { createTRPCQueryUtils, createTRPCReact } from '@trpc/react-query'
import { QueryClient } from '@tanstack/query-core'
import { observable } from '@trpc/server/observable'
import posthog from 'posthog-js'
import {
  dispatchLogout,
  getSessionToken,
  refreshTokens,
  sessionTokenIsExpiringSoon,
} from '@/config/auth'

export function getApiHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    authorization: `Bearer ${getSessionToken() || ''}`,
  }
  const sessionId = posthog.get_session_id?.()
  if (sessionId) headers['x-posthog-session-id'] = sessionId
  return headers
}

export type RouterInput = inferRouterInputs<TrpcRouter>
export type RouterOutput = inferRouterOutputs<TrpcRouter>

export const trpc = createTRPCReact<TrpcRouter>()

export const queryClient = new QueryClient()

const isUnauthorized = (err: unknown): err is TRPCClientError<TrpcRouter> =>
  err instanceof TRPCClientError && err.data?.code === 'UNAUTHORIZED'

// Catches UNAUTHORIZED, refreshes tokens (single-flight via auth.ts), retries once.
// On retry failure or refresh failure, dispatches a global logout event.
const refreshLink: TRPCLink<TrpcRouter> = () => {
  return ({ next, op }) => {
    return observable((observer) => {
      let currentSub = { unsubscribe: () => {} }

      // Don't try to refresh on the refresh/signOut calls themselves, nor on
      // credential checks: there UNAUTHORIZED means a wrong password or token,
      // not an expired session, and the forced logout would posthog.reset()
      // the visitor on every failed attempt.
      if (
        op.path === 'auth.refresh' ||
        op.path === 'auth.signOut' ||
        op.path === 'auth.authorize' ||
        op.path === 'auth.verifyUser'
      ) {
        currentSub = next(op).subscribe(observer)
        return () => currentSub.unsubscribe()
      }

      let retried = false
      const subscribe = () => {
        currentSub = next(op).subscribe({
          next: (value) => observer.next(value),
          complete: () => observer.complete(),
          error: (err) => {
            if (!retried && isUnauthorized(err)) {
              // Only attempt refresh if the local session token is actually
              // missing or expiring. A fresh token plus UNAUTHORIZED means
              // the procedure rejected for resource-level reasons; a refresh
              // wouldn't change anything and would needlessly rotate tokens.
              const token = getSessionToken()
              if (token && !sessionTokenIsExpiringSoon(token)) {
                observer.error(err as TRPCClientError<TrpcRouter>)
                return
              }
              retried = true
              refreshTokens()
                .then(() => subscribe())
                .catch(() => {
                  dispatchLogout()
                  observer.error(err as TRPCClientError<TrpcRouter>)
                })
              return
            }
            observer.error(err as TRPCClientError<TrpcRouter>)
          },
        })
      }

      subscribe()
      return () => currentSub.unsubscribe()
    })
  }
}

// A batch resolves only when its slowest procedure does. These can take
// seconds (LLM calls on a cache miss), so they go out on their own instead of
// holding back whatever else the page asked for in the same tick. On home,
// topic suggestions used to delay the moodboard list and usage limits by ~8s.
const UNBATCHED_PROCEDURES = new Set(['topicSuggestion.get'])

export const client = trpc.createClient({
  links: [
    refreshLink,
    splitLink({
      condition: (op) => UNBATCHED_PROCEDURES.has(op.path),
      true: httpLink({ url: env.VITE_BACKEND_URL, headers: getApiHeaders }),
      false: httpBatchLink({ url: env.VITE_BACKEND_URL, headers: getApiHeaders }),
    }),
  ],
})

export const clientUtils = createTRPCQueryUtils({ queryClient, client })

// Re-export for tree-shaking-friendly types
export type { TRPCError }
